import React, { useState, useEffect } from 'react';
import Layouts from '../Layouts';
import { Spin, Empty, Steps } from 'antd';
import { GoDotFill } from "react-icons/go";
import { MdSearch, MdReceipt } from "react-icons/md";
import { getCurrentUser } from '../../utils/auth';
import CartPlaceService from '../../Redux/Services/CartPlaceService';

function MyOrder() {
    const [orders, setOrders] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchOrders = async () => {
            const user = getCurrentUser();
            if (!user) return;
            const userId = user.userId || user.userCode || localStorage.getItem("userCode");
            if (!userId) return;

            setLoading(true);
            try {
                const response = await CartPlaceService.getMyOrderList(userId);
                if (response && response.data) {
                    setOrders(response.data);
                }
            } catch (err) {
                console.error("Failed to load orders:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchOrders();
    }, []);

    const filteredOrders = orders.filter(order => {
        const query = searchQuery.toLowerCase();
        return (
            (order.productCode || '').toLowerCase().includes(query) ||
            (order.txnId || '').toLowerCase().includes(query) ||
            (order.productCategoryCode || '').toLowerCase().includes(query)
        );
    });

    const getImgUrl = (imgName) => {
        if (!imgName) return '';
        if (imgName.startsWith('http') || imgName.startsWith('data:')) return imgName;
        return `${process.env.REACT_APP_IMAGE_URL || ""}${imgName}`;
    };

    return (
        <div className='min-h-screen bg-gray-50/50 py-12'>
            <Layouts>
                <div className='container mx-auto px-4 max-w-5xl'>
                    
                    {/* Header */}
                    <div className="text-center mb-10">
                        <span className="text-xs uppercase tracking-widest font-bold text-[#A17A17]">
                            Purchase History
                        </span>
                        <h1 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mt-1">
                            My Orders
                        </h1>
                        <div className="w-16 h-1 bg-[#A17A17] mx-auto mt-3 rounded-full" />
                    </div>

                    {/* Search Controls */}
                    {/* <div className='flex gap-2 justify-center max-w-xl mx-auto mb-8 relative'>
                        <div className="relative w-full">
                            <input 
                                type="text" 
                                placeholder="Search by Product Name, Code, or Transaction ID..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className='w-full border border-amber-200/80 rounded-xl p-3 pl-10 focus:outline-none focus:border-[#A17A17] focus:ring-1 focus:ring-[#A17A17] text-sm bg-white shadow-sm transition-all' 
                            />
                            <MdSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                        </div>
                    </div> */}

                    {/* Orders Body */}
                    {loading ? (
                        <div className="flex flex-col justify-center items-center py-24 bg-white rounded-3xl border border-amber-100 shadow-sm">
                            <Spin size="large" />
                            <p className="text-gray-500 mt-4 font-medium text-sm">Loading your orders...</p>
                        </div>
                    ) : filteredOrders.length === 0 ? (
                        <div className="bg-white rounded-3xl border border-amber-100 p-16 text-center shadow-sm">
                            <Empty 
                                description={
                                    <div className="mt-4">
                                        <h3 className="text-lg font-bold text-gray-800">No orders found</h3>
                                        <p className="text-gray-500 text-sm mt-1">
                                            {searchQuery ? "We couldn't find any orders matching your query." : "You haven't placed any orders yet."}
                                        </p>
                                    </div>
                                }
                            />
                        </div>
                    ) : (
                        <div className="flex flex-col gap-6">
                            {filteredOrders.map((order, index) => {
                                const formattedName = (order.productCode || 'PRODUCT').replace(/_/g, ' ');
                                const imageSrc = getImgUrl(order.productImage);
                                const isDelivered = order.status === 1;

                                return (
                                    <div 
                                        key={order.id || index} 
                                        className='bg-white border border-gray-100 rounded-3xl shadow-[0_10px_30px_rgba(0,0,0,0.02)] hover:shadow-[0_15px_35px_rgba(161,122,23,0.06)] transition-all p-5 md:p-6 overflow-hidden relative'
                                    >
                                        {/* Upper Meta Row */}
                                        <div className="flex flex-wrap justify-between items-center pb-4 border-b border-gray-100 mb-5 gap-3 text-xs md:text-sm text-gray-400">
                                            <div className="flex items-center gap-2">
                                                <MdReceipt className="text-lg text-[#A17A17]" />
                                                <span>TXN ID: <strong className="text-gray-700">{order.txnId}</strong></span>
                                            </div>
                                            <div>
                                                Ordered On: <strong className="text-gray-700">{order.createdDate?.slice(0, 10)}</strong>
                                            </div>
                                        </div>

                                        <div className='flex flex-col md:flex-row md:items-center justify-between gap-6'>
                                            
                                            {/* Product Details Section */}
                                            <div className='flex items-center gap-4 flex-1'>
                                                <div className="w-20 h-20 bg-gray-50 rounded-2xl border border-gray-100 flex-shrink-0 flex items-center justify-center overflow-hidden">
                                                    {imageSrc ? (
                                                        <img src={imageSrc} alt={formattedName} className='w-full h-full object-cover' />
                                                    ) : (
                                                        <div className="text-xs text-gray-400 text-center font-bold px-1">{order.productCategoryCode}</div>
                                                    )}
                                                </div>
                                                <div>
                                                    <h3 className='text-base font-bold text-gray-800 leading-tight'>
                                                        {formattedName}
                                                    </h3>
                                                    <p className='text-xs text-gray-400 mt-1 uppercase font-semibold tracking-wider'>
                                                        {order.productCategoryCode}
                                                    </p>
                                                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                                        <span>Quantity: <strong className="text-gray-800">{order.qty || 1}</strong></span>
                                                        <span>Paid: <strong className="text-[#A17A17] font-bold">INR {order.paidAmount || order.amount}</strong></span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Status Timeline */}
                                            <div className="flex flex-col justify-center min-w-[280px]">
                                                <div className='flex items-center gap-2 mb-2'>
                                                    <GoDotFill className={`text-base ${isDelivered ? 'text-green-500' : 'text-amber-500'}`} />
                                                    <span className='text-sm font-bold text-gray-800'>
                                                        {isDelivered ? 'Order Placed & Confirmed' : 'Pending Verification'}
                                                    </span>
                                                </div>
                                                <p className='text-xs text-gray-400 leading-relaxed max-w-[250px]'>
                                                    {isDelivered 
                                                        ? 'Your order has been verified and confirmed. A payment link has been dispatched to your mobile.' 
                                                        : 'Your order is currently undergoing inventory verification.'
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </Layouts>
        </div>
    );
}

export default MyOrder;



